import { useCallback } from 'react';
import styles from './FavoritePage.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFavorite } from './FavoriteContext';
import FlatCard from '../../components/CardsComponents/FlatCard/FlatCard';
import { FlatCardData } from '../../components/CardsComponents/types';
import FavoriteNoObjectsImage from '../../assets/images/FavoriteNoObjetcs.webp';
import Button from '../../components/UI/Button/Button';
import TotalObjects from '../../components/TotalObjects/TotalObjects';
import ParkingCard from '../../components/CardsComponents/ParkingCard/ParkingCard';
import BusinessCard from '../../components/CardsComponents/BusinessCard/BusinessCard';
import { BusinessCardData, ParkingCardData } from '../../components/CardsComponents/types';
import { Link } from 'react-router-dom';
import { PROJECTS_PAGE } from '../../router/paths';
import { Helmet } from 'react-helmet';

interface FavoriteElementsProps {
  favorites: (FlatCardData | ParkingCardData | BusinessCardData)[];
  title: string;
  type: 'flat' | 'parking' | 'business';
  showTitleAndLine: boolean;
  onRemoveAll: () => void;
}

const FavoriteElements = ({
  favorites,
  title,
  type,
  showTitleAndLine,
  onRemoveAll,
}: FavoriteElementsProps) => {
  const favorite = favorites.filter((item) => item.type === type);

  console.log(favorite);
  if (favorite.length === 0) return null;

  return (
    <div className={styles.track}>
      {showTitleAndLine && (
        <>
          <h3>{title}</h3>
          <span className={styles.line}></span>
        </>
      )}
      <TotalObjects objectLength={favorite.length} buttonColor={'orange'} onClick={onRemoveAll} />
      <div className={styles.favorite_cards}>
        {favorite.map((card: FlatCardData | ParkingCardData | BusinessCardData, index: number) => {
          if (type === 'flat') {
            return <FlatCard key={index} data={card as FlatCardData} isDiscounted={false} />;
          } else if (type === 'parking') {
            return <ParkingCard key={index} parkingData={card as ParkingCardData} isDiscounted={false} />;
          } else if (type === 'business') {
            return (
              <BusinessCard
                key={index}
                businessData={card as BusinessCardData}
                isDiscounted={false}
              />
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default function FavoritePage() {
  const { favorites, removeAllByType } = useFavorite();

  const removeAllByTypeHandler = useCallback(
    (type: 'flat' | 'parking' | 'business') => {
      removeAllByType(type);
    },
    [removeAllByType]
  );

  const hasOtherTypes = favorites.some((item) => item.type !== 'flat');

  return (
    <>
      <Helmet>
        <title>Избранное | Восток Строй</title>
        <meta name="description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="keywords" content="недвижимость, девелопер, Каспийск, новостройки, квартиры" />
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://vostokstroy.com/favorite" />

        <meta property="og:title" content="Избранное | Восток Строй" />
        <meta property="og:description" content="Мы начали св ой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/favorite" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Избранное | Восток Строй" />
        <meta name="twitter:description" content="Мы начали свой путь в 2014 году и реализовали 9 успешных проектов." />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />

        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      <Header theme='light'></Header>
      <div className={styles.wrapper}>
        <h2>Избранное</h2>
        {favorites.length > 0 ? (
          <div className={styles.elements}>
            <FavoriteElements
              favorites={favorites}
              title='Квартиры'
              type='flat'
              showTitleAndLine={hasOtherTypes}
              onRemoveAll={() => removeAllByTypeHandler('flat')}
            />
            <FavoriteElements
              favorites={favorites}
              title='Помещения для бизнеса'
              type='business'
              showTitleAndLine={true}
              onRemoveAll={() => removeAllByTypeHandler('business')}
            />
            <FavoriteElements
              favorites={favorites}
              title='Машино-места'
              type='parking'
              showTitleAndLine={true}
              onRemoveAll={() => removeAllByTypeHandler('parking')}
            />
          </div>
        ) : (
          <div className={styles.no_objects_wrapper}>
            <div className={styles.no_objects_text}>
              <p className='text_body_b2'>
                На&nbsp;этой странице сохраняются понравившиеся объекты&nbsp;&mdash; достаточно нажать на&nbsp;кнопку &laquo;Добавить в&nbsp;избранное&raquo; в&nbsp;карточке недвижимости и&nbsp;объект попадет сюда.
              </p>
            </div>
            <div className={styles.no_objects_image}>
              <img src={FavoriteNoObjectsImage} alt='Пример нажатой кнопки "Добавить в избранное"' />
            </div>
            <Link to={PROJECTS_PAGE}>
              <Button roundType='small_rounded' colorType='gray'>
                К проектам
              </Button>
            </Link>
          </div>
        )}
      </div>
      <Footer theme='light'></Footer>
    </>
  );
}
